// Buildings (04 §5.3) and Trading Port trades (04 §5.12).
import { z } from 'zod';
import { facilityTypeSchema, idSchema, isoDateTimeSchema, resourcesSchema } from './common.js';

/** Resource block of an owned system (04 §2.2): whole units; clients may animate from the rates. */
export const resourceBlockSchema = z.strictObject({
  amounts: resourcesSchema,
  ratesPerHour: resourcesSchema,
  caps: resourcesSchema,
  /** Raid-protected floor (`economy.raiding.protected_pct_of_storage_cap`). */
  protected: resourcesSchema,
  evaluatedAt: isoDateTimeSchema,
});
export type ResourceBlock = z.infer<typeof resourceBlockSchema>;

export const buildingQueueItemSchema = z.strictObject({
  id: idSchema,
  slot: z.number().int().positive(),
  facilityType: facilityTypeSchema,
  targetLevel: z.number().int().positive(),
  startedAt: isoDateTimeSchema,
  completesAt: isoDateTimeSchema,
});
export type BuildingQueueItem = z.infer<typeof buildingQueueItemSchema>;

export const upgradeBlockerSchema = z.discriminatedUnion('code', [
  z.strictObject({ code: z.literal('QUEUE_SLOTS_FULL') }),
  z.strictObject({ code: z.literal('LEVEL_CAP_REACHED'), maxLevel: z.number().int() }),
  z.strictObject({
    code: z.literal('REQUIREMENTS_NOT_MET'),
    requirement: z.enum(['command_center', 'not_in_slice']),
    requiredLevel: z.number().int().optional(),
  }),
]);
export type UpgradeBlocker = z.infer<typeof upgradeBlockerSchema>;

/**
 * The next level of one facility as the owner would start it now. `blocker` is the first queue or
 * level rule in the way (not resources — the client compares `cost` with the live amounts).
 * Facilities already at the maximum level have no entry.
 */
export const upgradeOptionSchema = z.strictObject({
  targetLevel: z.number().int().positive(),
  cost: resourcesSchema,
  artifacts: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  blocker: upgradeBlockerSchema.nullable(),
});
export type UpgradeOption = z.infer<typeof upgradeOptionSchema>;

export const upgradeResponseSchema = z.strictObject({
  queueItem: buildingQueueItemSchema,
  resources: resourceBlockSchema,
});
export type UpgradeResponse = z.infer<typeof upgradeResponseSchema>;

export const cancelBuildingResponseSchema = z.strictObject({
  cancelledId: idSchema,
  refunded: resourcesSchema,
});
export type CancelBuildingResponse = z.infer<typeof cancelBuildingResponseSchema>;

export const portTradeSchema = z.strictObject({
  id: idSchema,
  status: z.enum(['running', 'completed', 'cancelled']),
  resourcesIn: resourcesSchema,
  creditsOut: z.number().int().nonnegative(),
  startedAt: isoDateTimeSchema,
  completesAt: isoDateTimeSchema,
});
export type PortTrade = z.infer<typeof portTradeSchema>;

/** Current Trading Port terms of a system (slice: no E4, no Citadel multiplier). */
export const tradingPortTermsSchema = z.strictObject({
  level: z.number().int().nonnegative(),
  minResources: z.number().int().nonnegative(),
  maxResources: z.number().int().nonnegative(),
  creditsPer1000: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
});
export type TradingPortTerms = z.infer<typeof tradingPortTermsSchema>;

const wholeAmount = z.number().int().nonnegative().max(1_000_000_000);

export const portTradeRequestSchema = z.strictObject({
  resources: z.strictObject({ metal: wholeAmount, gas: wholeAmount, supplies: wholeAmount }),
});
export type PortTradeRequest = z.infer<typeof portTradeRequestSchema>;

export const portTradeResponseSchema = z.strictObject({
  trade: portTradeSchema,
  resources: resourceBlockSchema,
});
export type PortTradeResponse = z.infer<typeof portTradeResponseSchema>;

export const portTradeListResponseSchema = z.strictObject({ items: z.array(portTradeSchema) });
export type PortTradeListResponse = z.infer<typeof portTradeListResponseSchema>;
